import { type VariantProps, cva } from "class-variance-authority";
import { Check, ChevronDown, PlusIcon, Trash2, X, XIcon } from "lucide-react";
import {
  type ButtonHTMLAttributes,
  type ComponentType,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Button } from "../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { CustomToolTip } from "../ui/CustomToolTip";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { ScrollArea } from "../ui/ScrollArea";

import type { ButtonVariant, LabelValue } from "@/app/app.interface";
import { customToast } from "@/components/alerts/CustomToast";
import Icons from "@/components/Icons";
import Loading from "@/components/statePages/Loading";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/utils/core/generalUtils";

/* -------------------------------------------------------------------------- */
/*  Styles                                                                    */
/* -------------------------------------------------------------------------- */
const multiSelectVariants = cva(
  "transition ease-in-out delay-150 duration-300",
  {
    variants: {
      variant: {
        default:
          "border-foreground/10 text-foreground bg-[#F3F4F6] hover:bg-[#F3F4F6]",
        secondary:
          "border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        inverted: "inverted",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */
export interface MultiSelectProps
  extends
    ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  options: {
    label: string;
    value: string;
    icon?: ComponentType<{ className?: string }>;
    id?: string;
    isMandatory?: boolean;
  }[];
  onValueChange: (value: LabelValue[]) => void;
  defaultValue?: string[];
  Values?: LabelValue[];
  placeholder?: string;
  animation?: number;
  maxCount?: number;
  maxSelection?: number;
  modalPopover?: boolean;
  asChild?: boolean;
  className?: string;
  isLoading?: boolean;
  addButton?:
    | false
    | {
        label?: string;
        variant?: ButtonVariant;
        className?: string;
        handleClick?: () => void;
        icon?: string;
      };
  deleteAction?: (id: string) => void;
  side?: "top" | "right" | "bottom" | "left";
  node?: { position: "right"; node: ComponentType<{ id: string }> };
  isPopoverContentWidthFixed?: boolean;
  isFilter?: boolean;
  displayLabel?: string;
  displayAll?: boolean;
  showRemoveAllIcon?: boolean;
  searchPlaceholder?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  portal?: boolean;
}

interface SelectedValue extends LabelValue {
  isMandatory?: boolean;
  mandatoryMessage?: string;
}

type MultiSelectOption = MultiSelectProps["options"][number];

const isValidLabelValue = (
  option: MultiSelectOption | LabelValue | undefined,
): option is LabelValue =>
  typeof option?.value === "string" && typeof option?.label === "string";

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */
export const WpxMultiSelect = forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options = [],
      onValueChange,
      variant,
      defaultValue = [],
      placeholder = "Select options",
      animation = 0,
      searchPlaceholder = "Search options",
      maxCount = Number.MAX_SAFE_INTEGER,
      modalPopover = false,
      className,
      isLoading,
      Values,
      maxSelection,
      addButton,
      deleteAction,
      node,
      isPopoverContentWidthFixed = true,
      isFilter = false,
      displayAll = false,
      showRemoveAllIcon = true,
      displayLabel = "options",
      side,
      open: controlledOpen,
      onOpenChange,
      portal = false,
      ...props
    },
    ref,
  ) => {
    const [selected, setSelected] = useState<SelectedValue[]>([]);
    const [pending, setPending] = useState<LabelValue[]>([]);
    const [internalOpen, setInternalOpen] = useState(false);
    const open = controlledOpen ?? internalOpen;
    const [search, setSearch] = useState<string>("");

    const commandListRef = useRef<HTMLDivElement>(null);

    const mandatoryValues = useMemo(
      () =>
        new Set(
          (options ?? [])
            .filter((o) => o?.isMandatory)
            .map((o) => o?.value)
            .filter((v): v is string => Boolean(v)),
        ),
      [options],
    );

    const filteredOptions = useMemo(() => {
      const list = options ?? [];
      if (!search.trim()) {
        return list;
      }

      const searchLower = search.toLowerCase().trim();

      return list.filter(
        (option) =>
          option?.label?.toLowerCase()?.includes(searchLower) ?? false,
      );
    }, [options, search]);

    const ensureMandatory = (list: LabelValue[]): LabelValue[] => {
      const safeList = list ?? [];
      const mandatoryOptions = (options ?? []).filter(
        (option) => option?.isMandatory && isValidLabelValue(option),
      );
      const withoutMandatory = safeList.filter(
        (selectedValue) =>
          selectedValue?.value && !mandatoryValues.has(selectedValue.value),
      );

      return [...mandatoryOptions, ...withoutMandatory];
    };

    useEffect(() => {
      const next: LabelValue[] = [];
      const seen = new Set<string>();
      const optionsMap = new Map(
        (options ?? [])
          .filter((option) => isValidLabelValue(option))
          .map((option) => [option.value, option] as const),
      );

      const addOption = (val?: string): void => {
        if (!val) {
          return;
        }
        const opt = optionsMap.get(val);
        if (opt && !seen.has(val)) {
          seen.add(val);
          next.push(opt);
        }
      };

      if (Values?.length && typeof Values[0] === "object") {
        Values.forEach((v) => addOption(v?.value));
      } else if (Array.isArray(defaultValue)) {
        defaultValue.forEach(addOption);
      }

      setSelected(ensureMandatory(next));
    }, [Values, defaultValue, options]);

    useEffect(() => {
      if (open) {
        setPending(selected);
      }
    }, [open, selected, setPending]);

    useEffect(() => {
      const allowed = new Set(
        (options ?? []).map((o) => o?.value).filter(Boolean) as string[],
      );
      const clean = (list: LabelValue[]): LabelValue[] =>
        (list ?? []).filter((v) => v?.value && allowed.has(v.value));

      const s = clean(selected);
      const p = clean(pending);

      if (s.length !== selected.length || p.length !== pending.length) {
        setSelected(ensureMandatory(s));
        setPending(ensureMandatory(p));
        onValueChange(ensureMandatory(s));
      }
    }, [options]);

    const toggleOption = (opt?: SelectedValue): void => {
      if (!opt?.value) {
        return;
      }
      if (opt.isMandatory) {
        customToast.info("Required choice can’t be removed.");

        return;
      }

      setPending((prev) => {
        const ex = prev.some((v) => v.value === opt.value);
        const next = ex
          ? prev.filter((v) => v.value !== opt.value)
          : [...prev, opt];

        if (!isFilter) {
          const withM = ensureMandatory(next);
          setSelected(withM);
          onValueChange(withM);
        }

        return next;
      });
    };

    const handleApply = (): void => {
      const withM = ensureMandatory(pending);
      setSelected(withM);
      onValueChange(withM);
      setInternalOpen(false);
      onOpenChange?.(false);
    };

    const handleClear = (): void => {
      const mOnly = options.filter((o) => o.isMandatory);
      setPending(mOnly);
      setSelected(mOnly);
      onValueChange(mOnly);
    };

    const removeSingle = (idx: number): void => {
      const opt = selected?.[idx];
      if (!opt || opt.isMandatory) {
        return;
      }

      const next = selected.filter((_, i) => i !== idx);
      const withM = ensureMandatory(next);
      setSelected(withM);
      setPending(withM);
      onValueChange(withM);
    };

    const BadgeItem = ({
      option,
      onRemove,
    }: {
      option?: (typeof options)[number];
      onRemove: () => void;
    }): JSX.Element | null => {
      if (!option) {
        return null;
      }

      return (
        <Badge
          key={option.value}
          className={cn(
            "font-normal whitespace-normal text-xs flex items-center max-w-[180px]",
            multiSelectVariants({ variant }),
          )}
          style={{ animationDuration: `${animation}s` }}
        >
          {option?.icon && <option.icon className='w-4 h-4 mr-2' />}
          <span className='truncate flex-1 min-w-0' title={option?.label}>
            {option?.label}
          </span>
          {!option?.isMandatory && (
            <X
              className='w-3 h-3 cursor-pointer hover:text-destructive flex-shrink-0'
              onClick={(ev) => {
                ev.stopPropagation();
                onRemove();
              }}
            />
          )}
        </Badge>
      );
    };

    const SummaryTrigger = ({ count }: { count: number }): JSX.Element => (
      <CustomToolTip
        toolTipContent={
          <div className='flex flex-col gap-1'>
            {selected.map((v) => (
              <span key={v.value} className='text-xs'>
                {v.label}
              </span>
            ))}
          </div>
        }
        toolTipTrigger={
          <span className='text-sm text-foreground font-normal lowercase'>
            {count} {displayLabel} selected
          </span>
        }
      />
    );

    const TriggerContent = (): JSX.Element => {
      if (!selected?.length) {
        return (
          <div className='flex items-center justify-between w-full'>
            <span className='text-sm text-black font-normal text-left mx-2 w-[75%] truncate opacity-30'>
              {placeholder}
            </span>
            <ChevronDown className='w-4 h-4 mx-2 cursor-pointer text-muted-foreground' />
          </div>
        );
      }

      const summary = selected.length > maxCount && !displayAll;

      return (
        <div className='flex items-center justify-between w-full'>
          <div className='flex flex-wrap items-center gap-1 w-full'>
            {summary ? (
              <SummaryTrigger count={selected.length} />
            ) : (
              selected.map((selectedValue, idx) => {
                if (!selectedValue?.value) {
                  return null;
                }

                const matched = (options ?? []).find(
                  (option) => option?.value === selectedValue.value,
                );
                if (!matched) {
                  return null;
                }

                return (
                  <BadgeItem
                    key={selectedValue.value}
                    option={matched}
                    onRemove={() => removeSingle(idx)}
                  />
                );
              })
            )}
          </div>

          <div className='flex items-center'>
            {showRemoveAllIcon && selected.length > 0 && (
              <XIcon
                className='h-4 mx-2 cursor-pointer text-muted-foreground'
                onClick={(ev) => {
                  ev.stopPropagation();
                  handleClear();
                }}
              />
            )}
            {!summary ||
              (selected.length === 0 && (
                <ChevronDown className='h-4 cursor-pointer text-muted-foreground' />
              ))}
          </div>
        </div>
      );
    };

    return (
      <Popover
        open={open}
        onOpenChange={(open: boolean) => {
          setInternalOpen(open);
          onOpenChange?.(open);
          setSearch("");
        }}
        modal={modalPopover}
      >
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            {...props}
            onClick={() => {
              const nextOpen = !open;
              setInternalOpen(nextOpen);
              onOpenChange?.(nextOpen);
            }}
            className={cn(
              "flex w-full p-1 px-1 rounded-md border min-h-10 h-auto items-center justify-between bg-inherit hover:bg-inherit active:scale-100",
              className,
            )}
            onKeyUp={(e: React.KeyboardEvent<HTMLButtonElement>) => {
              if (e.key === "Tab") {
                setInternalOpen(true);
                onOpenChange?.(true);
              }
              if (e.key === "Escape") {
                setInternalOpen(false);
                onOpenChange?.(false);
              }
            }}
          >
            <TriggerContent />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className={cn(
            "p-2 -mt-2 border border-input rounded-[0.5rem] shadow-select",
            isPopoverContentWidthFixed && "popover-content-width-full",
          )}
          align='start'
          side={side}
          onEscapeKeyDown={() => {
            setInternalOpen(false);
            onOpenChange?.(false);
          }}
          portal={portal}
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={searchPlaceholder}
              onValueChange={(value: string) => {
                setSearch(value);
                setTimeout(() => {
                  commandListRef.current?.scrollTo({
                    top: 0,
                    behavior: "smooth",
                  });
                });
              }}
            />

            <CommandList className='select-scrollbar-hide' ref={commandListRef}>
              <CommandEmpty firstRender>No results found.</CommandEmpty>
              <CommandGroup className='px-0 !mx-0'>
                {isLoading ? (
                  <Loading />
                ) : (
                  <ScrollArea className='overflow-y-scroll select-scrollbar-hide'>
                    {filteredOptions.map((opt, idx) => {
                      if (!opt?.value) {
                        return null;
                      }
                      const isSelected = pending.some(
                        (v) => v?.value === opt.value,
                      );
                      const disabled =
                        !opt.isMandatory &&
                        maxSelection &&
                        (selected?.length ?? 0) >= maxSelection &&
                        !isSelected;

                      return (
                        <CommandItem
                          key={opt.value}
                          value={`${CSS.escape(opt.label ?? opt.value)}-${idx}`}
                          onSelect={() => toggleOption(opt)}
                          className='cursor-pointer content-stretch group'
                          disabled={Boolean(disabled)}
                        >
                          <div className='flex items-center justify-between w-full'>
                            <div className='flex items-center flex-1 gap-2'>
                              <div className='flex items-center justify-center min-w-4 h-4 border rounded-sm'>
                                {isSelected && <Check className='w-3 h-3' />}
                              </div>
                              {opt.icon && (
                                <opt.icon className='w-4 h-4 mr-2 text-muted-foreground' />
                              )}
                              <span>{opt.label ?? ""}</span>
                            </div>

                            {node?.position === "right" && (
                              <node.node id={opt.value} />
                            )}
                          </div>

                          {deleteAction && (
                            <div
                              className='absolute invisible p-1 cursor-pointer right-3 top-2 group-hover:visible'
                              onClick={(ev) => {
                                ev.stopPropagation();
                                deleteAction?.(opt.id ?? opt.value);
                              }}
                            >
                              <Trash2 name='trash' color='red' size={18} />
                            </div>
                          )}
                        </CommandItem>
                      );
                    })}
                  </ScrollArea>
                )}
              </CommandGroup>
            </CommandList>

            {addButton && (
              <Button
                type='button'
                className={cn(
                  "flex justify-center items-center w-full",
                  addButton.className,
                )}
                variant={addButton.variant || "secondary"}
                onClick={addButton.handleClick}
              >
                {addButton.icon ? (
                  <Icons name={addButton.icon} size={18} color='#fff' />
                ) : (
                  <PlusIcon size={18} strokeWidth={1.8} className='mx-1' />
                )}
                <p className={addButton.icon && "px-2"}>{addButton.label}</p>
              </Button>
            )}
          </Command>

          {isFilter && (
            <div className='flex items-center justify-end gap-2 pt-2 border-t'>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='h-8'
                onClick={handleClear}
              >
                Reset
              </Button>
              <Button
                type='button'
                size='sm'
                className='h-8'
                disabled={!selected?.length && !pending?.length}
                onClick={handleApply}
              >
                Apply
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    );
  },
);

WpxMultiSelect.displayName = "WpxMultiSelect";
